import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Sprout, ArrowLeft, Users, CheckCircle, Clock, AlertTriangle,
  Loader2, ChevronRight, Eye, FileText, Download, Shield,
  UserCheck, AlertCircle, X, Printer
} from 'lucide-react';
import {
  getOnboardingOverview, getStaffOnboarding,
  approveOnboardingSection, rejectOnboardingSection,
  fullyApproveStaff, approveAllSections, downloadOnboardingDocument,
  getOnboardingCompliance, exportStaffOnboardingCSV
} from '../services/api';
import { toast } from 'sonner';

const statusColors = {
  not_started: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-blue-100 text-blue-700',
  in_review: 'bg-amber-100 text-amber-700',
  submitted: 'bg-amber-100 text-amber-700',
  ready_for_approval: 'bg-purple-100 text-purple-700',
  fully_approved: 'bg-emerald-100 text-emerald-700',
  approved: 'bg-emerald-100 text-emerald-700',
};

const statusLabels = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  in_review: 'Under Review',
  submitted: 'Submitted',
  ready_for_approval: 'Ready for Final Approval',
  fully_approved: 'Fully Approved',
  approved: 'Approved',
};

const AdminOnboarding = () => {
  const [overview, setOverview] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffDetail, setStaffDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectNotes, setRejectNotes] = useState({});
  const [showRejectInput, setShowRejectInput] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [compliance, setCompliance] = useState(null);
  const navigate = useNavigate();

  const fetchOverview = useCallback(async () => {
    try {
      const [overviewRes, complianceRes] = await Promise.all([
        getOnboardingOverview(),
        getOnboardingCompliance().catch(() => ({ data: null }))
      ]);
      setOverview(overviewRes.data);
      setCompliance(complianceRes.data);
    } catch (err) {
      toast.error('Failed to load onboarding overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const openStaffDetail = async (userId) => {
    setSelectedStaff(userId);
    setDetailLoading(true);
    try {
      const res = await getStaffOnboarding(userId);
      setStaffDetail(res.data);
    } catch (err) {
      toast.error('Failed to load staff details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApproveSection = async (sectionId) => {
    setActionLoading(sectionId);
    try {
      await approveOnboardingSection(selectedStaff, sectionId);
      toast.success('Section approved');
      openStaffDetail(selectedStaff);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectSection = async (sectionId) => {
    const notes = rejectNotes[sectionId] || '';
    if (!notes.trim()) {
      toast.error('Please provide feedback before returning the section');
      return;
    }
    setActionLoading(sectionId);
    try {
      await rejectOnboardingSection(selectedStaff, sectionId, notes);
      toast.success('Section returned to staff with feedback');
      setShowRejectInput(null);
      setRejectNotes(prev => ({ ...prev, [sectionId]: '' }));
      openStaffDetail(selectedStaff);
    } catch (err) {
      toast.error('Failed to reject section');
    } finally {
      setActionLoading(null);
    }
  };

  const handleFullyApprove = async () => {
    setActionLoading('full');
    try {
      await fullyApproveStaff(selectedStaff);
      toast.success('Staff fully approved and cleared to work!');
      openStaffDetail(selectedStaff);
      fetchOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot fully approve yet');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveAllSections = async () => {
    setActionLoading('all_sections');
    try {
      const res = await approveAllSections(selectedStaff);
      toast.success(res.data.message || 'All sections approved');
      openStaffDetail(selectedStaff);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve all sections');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0FAF7]">
        <Loader2 className="w-8 h-8 animate-spin text-[#5BA4A4]" />
      </div>
    );
  }

  // Staff detail view
  if (selectedStaff && staffDetail) {
    return (
      <StaffDetailView
        staffDetail={staffDetail}
        staffId={selectedStaff}
        detailLoading={detailLoading}
        actionLoading={actionLoading}
        showRejectInput={showRejectInput}
        rejectNotes={rejectNotes}
        setShowRejectInput={setShowRejectInput}
        setRejectNotes={setRejectNotes}
        onBack={() => { setSelectedStaff(null); setStaffDetail(null); fetchOverview(); }}
        onApproveSection={handleApproveSection}
        onRejectSection={handleRejectSection}
        onFullyApprove={handleFullyApprove}
        onApproveAllSections={handleApproveAllSections}
      />
    );
  }

  // Overview list
  const stats = overview?.stats || {};
  const staff = overview?.staff || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0FAF7] via-white to-[#E8F8F5]">
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#A8E6CF]/30 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-[#5BA4A4] to-[#A8E6CF] rounded-xl flex items-center justify-center">
              <Sprout className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#2D4A4A]">Staff Onboarding</h1>
              <p className="text-xs text-[#5A7A7A]">Safer Recruitment Management</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/scr')}
              className="text-xs border-[#A8E6CF] text-[#5BA4A4] hover:bg-[#F0FAF7]"
              data-testid="view-scr-btn"
            >
              <Printer className="w-3 h-3 mr-1" /> Single Central Record
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-[#5A7A7A]" data-testid="back-to-admin">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Total Staff" value={stats.total_staff} icon={Users} color="text-[#5BA4A4]" bgColor="bg-[#E8F8F5]" />
          <StatCard label="Fully Approved" value={stats.fully_approved} icon={CheckCircle} color="text-emerald-600" bgColor="bg-emerald-50" />
          <StatCard label="Pending Review" value={stats.pending_review} icon={Clock} color="text-amber-600" bgColor="bg-amber-50" />
          <StatCard label="Incomplete" value={stats.incomplete} icon={AlertTriangle} color="text-red-500" bgColor="bg-red-50" />
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 border-b border-[#A8E6CF]/30 pb-1">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors ${activeTab === 'overview' ? 'bg-white text-[#5BA4A4] border border-b-0 border-[#A8E6CF]/30' : 'text-[#5A7A7A] hover:text-[#5BA4A4]'}`}
            data-testid="tab-overview"
          >
            <Users className="w-3 h-3 inline mr-1" /> Staff Overview
          </button>
          <button
            onClick={() => setActiveTab('compliance')}
            className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors ${activeTab === 'compliance' ? 'bg-white text-[#5BA4A4] border border-b-0 border-[#A8E6CF]/30' : 'text-[#5A7A7A] hover:text-[#5BA4A4]'}`}
            data-testid="tab-compliance"
          >
            <Shield className="w-3 h-3 inline mr-1" /> Compliance Tracking
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <Card className="border border-[#A8E6CF]/30 bg-white/80">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-[#2D4A4A]">All Staff Onboarding</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {staff.length === 0 ? (
                <div className="text-center py-10 text-[#5A7A7A]">
                  <Users className="w-10 h-10 mx-auto mb-2 text-[#A8E6CF]" />
                  <p className="text-sm">No staff members found</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {staff.map(s => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between p-4 hover:bg-[#F0FAF7]/50 cursor-pointer transition-colors"
                      onClick={() => openStaffDetail(s.id)}
                      data-testid={`staff-onboarding-${s.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#2D4A4A] truncate">{s.name}</p>
                        <p className="text-xs text-[#5A7A7A]">{s.email}</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="hidden sm:block w-24">
                          <Progress value={s.progress} className="h-1.5" />
                          <p className="text-[10px] text-[#5A7A7A] mt-0.5 text-right">{s.approved_sections}/{s.total_sections}</p>
                        </div>
                        <Badge className={`text-[10px] ${statusColors[s.overall_status] || statusColors.not_started}`}>
                          {statusLabels[s.overall_status] || 'Not Started'}
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Compliance Tracking Tab */}
        {activeTab === 'compliance' && compliance && (
          <ComplianceTrackingView compliance={compliance} />
        )}
      </main>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color, bgColor }) => (
  <Card className="border border-[#A8E6CF]/20">
    <CardContent className="p-3 flex items-center gap-3">
      <div className={`w-9 h-9 ${bgColor} rounded-lg flex items-center justify-center`}>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <div>
        <p className="text-xl font-bold text-[#2D4A4A]">{value || 0}</p>
        <p className="text-[10px] text-[#5A7A7A]">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const ComplianceTrackingView = ({ compliance }) => {
  const cStats = compliance?.stats || {};
  const staff = compliance?.staff || [];

  return (
    <div className="space-y-4">
      {/* Compliance Alert Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Card className="border border-red-200 bg-red-50">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-red-700">{cStats.missing_dbs || 0}</p>
            <p className="text-[10px] text-red-600">Missing DBS</p>
          </CardContent>
        </Card>
        <Card className="border border-amber-200 bg-amber-50">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-amber-700">{cStats.dbs_expiring_soon || 0}</p>
            <p className="text-[10px] text-amber-600">DBS Expiring (90d)</p>
          </CardContent>
        </Card>
        <Card className="border border-amber-200 bg-amber-50">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-amber-700">{cStats.rtw_expiring_soon || 0}</p>
            <p className="text-[10px] text-amber-600">RTW Expiring (90d)</p>
          </CardContent>
        </Card>
      </div>

      {/* Compliance Table */}
      <Card className="border border-[#A8E6CF]/30 bg-white/80">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-[#2D4A4A]">DBS & Right to Work Tracking</CardTitle>
          <CardDescription className="text-xs">Track expiry dates and missing compliance items</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="compliance-table">
              <thead>
                <tr className="bg-[#F0FAF7] border-b border-[#A8E6CF]/30">
                  <th className="text-left p-3 text-[#2D4A4A] font-medium">Staff</th>
                  <th className="text-left p-3 text-[#2D4A4A] font-medium">DBS Status</th>
                  <th className="text-left p-3 text-[#2D4A4A] font-medium">DBS Expiry</th>
                  <th className="text-left p-3 text-[#2D4A4A] font-medium">RTW Expiry</th>
                  <th className="text-left p-3 text-[#2D4A4A] font-medium">Missing</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {staff.map(s => (
                  <tr key={s.id} className="hover:bg-[#F0FAF7]/50">
                    <td className="p-3">
                      <p className="font-medium text-[#2D4A4A]">{s.name}</p>
                      <p className="text-[10px] text-[#5A7A7A]">{s.email}</p>
                    </td>
                    <td className="p-3">
                      <Badge className={`text-[10px] ${
                        s.dbs_status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        s.dbs_status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {s.dbs_number ? `#${s.dbs_number}` : s.dbs_status || 'None'}
                      </Badge>
                    </td>
                    <td className="p-3">
                      {s.dbs_expiry ? (
                        <span className={s.dbs_days_until <= 0 ? 'text-red-600 font-medium' : s.dbs_days_until <= 90 ? 'text-amber-600' : 'text-[#2D4A4A]'}>
                          {new Date(s.dbs_expiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {s.dbs_days_until <= 0 && <span className="ml-1 text-[10px]">(EXPIRED)</span>}
                          {s.dbs_days_until > 0 && s.dbs_days_until <= 90 && <span className="ml-1 text-[10px]">({s.dbs_days_until}d)</span>}
                        </span>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="p-3">
                      {s.rtw_expiry ? (
                        <span className={s.rtw_days_until <= 0 ? 'text-red-600 font-medium' : s.rtw_days_until <= 90 ? 'text-amber-600' : 'text-[#2D4A4A]'}>
                          {new Date(s.rtw_expiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {s.rtw_days_until <= 0 && <span className="ml-1 text-[10px]">(EXPIRED)</span>}
                          {s.rtw_days_until > 0 && s.rtw_days_until <= 90 && <span className="ml-1 text-[10px]">({s.rtw_days_until}d)</span>}
                        </span>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="p-3">
                      {s.missing_count > 0 ? (
                        <span className="text-red-600 font-medium">{s.missing_count} sections</span>
                      ) : (
                        <span className="text-emerald-600">Complete</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const StaffDetailView = ({
  staffDetail, staffId, detailLoading, actionLoading, showRejectInput, rejectNotes,
  setShowRejectInput, setRejectNotes, onBack, onApproveSection, onRejectSection, onFullyApprove, onApproveAllSections
}) => {
  if (detailLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0FAF7]">
        <Loader2 className="w-8 h-8 animate-spin text-[#5BA4A4]" />
      </div>
    );
  }

  const { user, sections, overall_status, audit_log } = staffDetail;
  const allApproved = sections.every(s => s.status === 'approved');
  const isFullyApproved = overall_status === 'fully_approved';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0FAF7] via-white to-[#E8F8F5]">
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#A8E6CF]/30 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-overview">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-base font-bold text-[#2D4A4A]">{user?.name}</h1>
              <p className="text-xs text-[#5A7A7A]">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isFullyApproved && !allApproved && (
              <Button
                size="sm"
                onClick={onApproveAllSections}
                disabled={actionLoading === 'all_sections'}
                className="bg-[#5BA4A4] hover:bg-[#4A8F8F] text-white text-xs"
                data-testid="approve-all-sections-btn"
              >
                {actionLoading === 'all_sections' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                Approve All Sections
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportStaffOnboardingCSV(staffId, user?.name)}
              className="text-xs border-[#A8E6CF] text-[#5BA4A4]"
              data-testid="export-staff-csv"
            >
              <Download className="w-3 h-3 mr-1" /> Export CSV
            </Button>
            <Badge className={statusColors[overall_status] || statusColors.not_started} data-testid="staff-overall-status">
              {statusLabels[overall_status] || 'Not Started'}
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {allApproved && !isFullyApproved && (
          <Alert className="border-purple-200 bg-purple-50">
            <Shield className="w-4 h-4 text-purple-600" />
            <AlertDescription className="text-purple-800 text-sm flex items-center justify-between">
              <span>All sections approved. Ready for final clearance.</span>
              <Button
                size="sm"
                onClick={onFullyApprove}
                disabled={actionLoading === 'full'}
                className="bg-emerald-600 hover:bg-emerald-700 text-white ml-3"
                data-testid="fully-approve-btn"
              >
                {actionLoading === 'full' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <UserCheck className="w-3 h-3 mr-1" />}
                Grant Full Approval
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {isFullyApproved && (
          <Alert className="border-emerald-200 bg-emerald-50">
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            <AlertDescription className="text-emerald-800 text-sm">
              This staff member is fully approved and cleared to work.
            </AlertDescription>
          </Alert>
        )}

        {/* Sections */}
        <div className="space-y-3">
          {sections.map(section => (
            <Card key={section.id} className={`border ${section.status === 'submitted' ? 'border-amber-300 bg-amber-50/30' : 'border-[#A8E6CF]/30 bg-white/80'}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-[#2D4A4A]">{section.order}. {section.title}</h3>
                    <Badge className={`text-[10px] ${statusColors[section.status] || statusColors.not_started}`}>
                      {statusLabels[section.status] || 'Not Started'}
                    </Badge>
                  </div>
                </div>

                {/* Show submitted data */}
                {section.data && Object.keys(section.data).length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-2">
                    {Object.entries(section.data).map(([key, value]) => {
                      // Render arrays (references, employment entries, education entries) as cards
                      if (Array.isArray(value) && value.length > 0) {
                        return (
                          <div key={key}>
                            <p className="text-[#5A7A7A] capitalize font-medium mb-1.5">{key.replace(/_/g, ' ')} ({value.length})</p>
                            <div className="space-y-2 pl-2">
                              {value.map((item, idx) => (
                                <div key={idx} className="bg-white rounded border border-gray-200 p-2.5 space-y-1">
                                  <p className="text-[10px] text-[#5BA4A4] font-semibold mb-1">#{idx + 1}</p>
                                  {typeof item === 'object' && item !== null ? (
                                    Object.entries(item).map(([k, v]) => (
                                      <div key={k} className="flex gap-2">
                                        <span className="text-[#5A7A7A] capitalize min-w-[120px]">{k.replace(/_/g, ' ')}:</span>
                                        <span className="text-[#2D4A4A] font-medium">{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v || '—')}</span>
                                      </div>
                                    ))
                                  ) : (
                                    <span className="text-[#2D4A4A]">{String(item)}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      // Render objects (single nested object) as expanded fields
                      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                        return (
                          <div key={key}>
                            <p className="text-[#5A7A7A] capitalize font-medium mb-1">{key.replace(/_/g, ' ')}</p>
                            <div className="pl-2 space-y-1">
                              {Object.entries(value).map(([k, v]) => (
                                <div key={k} className="flex gap-2">
                                  <span className="text-[#5A7A7A] capitalize min-w-[120px]">{k.replace(/_/g, ' ')}:</span>
                                  <span className="text-[#2D4A4A] font-medium">{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v || '—')}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={key} className="flex gap-2">
                          <span className="text-[#5A7A7A] capitalize min-w-[120px]">{key.replace(/_/g, ' ')}:</span>
                          <span className="text-[#2D4A4A] font-medium">{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Show warning when section is submitted but has no data */}
                {section.status === 'submitted' && (!section.data || Object.keys(section.data).length === 0) && (
                  <div className="bg-amber-50 rounded-lg p-2.5 text-xs text-amber-700 border border-amber-200">
                    Submitted with no data — consider returning with feedback to ask the staff member to complete the form fields.
                  </div>
                )}

                {/* Documents */}
                {section.documents && section.documents.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-[#5A7A7A]">Documents:</p>
                    {section.documents.map(doc => (
                      <div key={doc.file_id} className="flex items-center gap-2 text-xs p-2 bg-white rounded border border-gray-100">
                        <FileText className="w-3.5 h-3.5 text-[#5BA4A4]" />
                        <span className="truncate flex-1">{doc.original_filename}</span>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-8 px-3 text-xs border-[#A8E6CF] text-[#5BA4A4] hover:bg-[#F0FAF7]" 
                          onClick={() => downloadOnboardingDocument(doc.file_id, doc.original_filename)}
                          data-testid={`download-doc-${doc.file_id}`}
                        >
                          <Download className="w-3.5 h-3.5 mr-1" /> Download
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Admin Actions - show for any non-approved section */}
                {section.status !== 'approved' && !isFullyApproved && (
                  <div className="flex flex-col gap-2 pt-2 border-t border-gray-100">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => onApproveSection(section.id)}
                        disabled={actionLoading === section.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                        data-testid={`approve-section-${section.id}`}
                      >
                        {actionLoading === section.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                        Approve
                      </Button>
                      {section.status === 'submitted' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowRejectInput(showRejectInput === section.id ? null : section.id)}
                          className="border-red-300 text-red-600 hover:bg-red-50 text-xs"
                          data-testid={`reject-section-${section.id}`}
                        >
                          <X className="w-3 h-3 mr-1" /> Return with Feedback
                        </Button>
                      )}
                    </div>
                    {showRejectInput === section.id && (
                      <div className="flex gap-2">
                        <Textarea
                          value={rejectNotes[section.id] || ''}
                          onChange={e => setRejectNotes(prev => ({ ...prev, [section.id]: e.target.value }))}
                          placeholder="Explain what needs to be corrected..."
                          rows={2}
                          className="text-xs"
                          data-testid={`reject-notes-${section.id}`}
                        />
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => onRejectSection(section.id)}
                          disabled={actionLoading === section.id}
                          className="shrink-0 self-end text-xs"
                          data-testid={`confirm-reject-${section.id}`}
                        >
                          Send
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {section.admin_notes && (
                  <p className="text-[10px] text-[#5A7A7A] italic">Admin notes: {section.admin_notes}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Audit Log */}
        {audit_log && audit_log.length > 0 && (
          <Card className="border border-[#A8E6CF]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#2D4A4A]">Audit Trail</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="space-y-2">
                {audit_log.map((entry, idx) => (
                  <div key={idx} className="flex gap-3 text-xs">
                    <span className="text-[#5A7A7A] min-w-[130px] shrink-0">
                      {new Date(entry.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className={`font-medium ${entry.action === 'approved' || entry.action === 'fully_approved' ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {entry.action === 'fully_approved' ? 'Fully Approved' : `${entry.section_id} ${entry.action}`}
                    </span>
                    <span className="text-[#5A7A7A]">by {entry.admin_name}</span>
                    {entry.notes && <span className="text-gray-400 italic truncate">"{entry.notes}"</span>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default AdminOnboarding;
